import type { TabOption } from '@/components/chariot-ui';
import type {
  MainTabKey,
  POEContent,
  SectionContent,
} from '@/sections/poe/types/poe.types';
import { formatDate } from '@/utils/date.util';

export const processHadrianDataSource = (
  poeData: POEContent,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>,
  copyContents?: Record<string, string>
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = poeData as Record<string, any>;
  const evidence = data?.evidence || {};
  const req = evidence?.request || {};
  const res = evidence?.response || {};

  const requestHeaders = req?.headers || {};
  const responseHeaders = res?.headers || {};
  const requestBody = req?.body || '';
  let responseBody = res?.body || '';
  if (typeof responseBody === 'string') {
    try {
      responseBody = JSON.stringify(JSON.parse(responseBody), null, 2);
    } catch {
      // keep raw string when not JSON
    }
  }

  sections['hadrian_summary'] = (
    <div className="border-chariot-stroke overflow-hidden rounded-lg border">
      <table className="w-full">
        <tbody>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
              Category
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.category || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Finding
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.name || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Endpoint
            </td>
            <td className="text-chariot-text-primary px-4 py-3 font-mono text-sm">
              {`${data?.method || ''} ${data?.endpoint || ''}`.trim() || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Attacker Role
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.attacker_role || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Severity
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.severity || '-'}
            </td>
          </tr>
          <tr className="border-chariot-stroke border-b last:border-b-0">
            <td className="bg-chariot-background-secondary text-chariot-text-secondary px-4 py-3 font-medium">
              Timestamp
            </td>
            <td className="text-chariot-text-primary px-4 py-3">
              {data?.timestamp ? formatDate(data.timestamp) : '-'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
  tempTabs['INFORMATION'].push({ label: 'Summary', value: 'hadrian_summary' });

  if (Array.isArray(data?.request_ids) && data.request_ids.length > 0) {
    sections['hadrian_request_ids'] = (
      <div className="space-y-2">
        {data.request_ids.map((id: string) => (
          <div
            key={id}
            className="text-chariot-text-secondary flex items-center gap-2 rounded border px-2 py-1 font-mono text-xs"
          >
            <span>{id}</span>
          </div>
        ))}
      </div>
    );
    tempTabs['DETECTION'].push({
      label: 'Request IDs',
      value: 'hadrian_request_ids',
    });
  }

  sections['hadrian_detection'] =
    data?.description ||
    data?.id ||
    'No additional detection metadata provided.';
  tempTabs['DETECTION'].push({
    label: 'Detection',
    value: 'hadrian_detection',
  });

  const requestRaw = JSON.stringify(
    {
      method: req?.method || data?.method || '',
      url: req?.url || data?.endpoint || '',
      headers: requestHeaders,
      body: requestBody,
    },
    null,
    2
  );
  sections['hadrian_request'] = requestRaw;
  tempTabs['REQUEST'].push({ label: 'Request', value: 'hadrian_request' });
  if (copyContents) copyContents['hadrian_request'] = requestRaw;

  const responseRaw = JSON.stringify(
    {
      status_code: res?.status_code,
      headers: responseHeaders,
      body: responseBody,
      body_hash: res?.body_hash,
      size: res?.size,
      truncated: res?.truncated,
    },
    null,
    2
  );
  sections['hadrian_response'] = responseRaw;
  tempTabs['RESPONSE'].push({ label: 'Response', value: 'hadrian_response' });
  if (copyContents) copyContents['hadrian_response'] = responseRaw;

  // Keep raw POE for debugging
  const rawData = JSON.stringify(data, null, 2);
  sections['hadrian_raw_data'] = rawData;
  tempTabs['INFORMATION'].push({
    label: 'Raw Data',
    value: 'hadrian_raw_data',
  });
  if (copyContents) copyContents['hadrian_raw_data'] = rawData;
};
